import type {
  MotivoAjusteInventario,
  TipoAjusteConMotivo,
} from '../constants/motivosAjusteInventario';

export type TipoAjusteInventarioUi = 'positivo' | 'negativo' | 'transferencia';

export function requiereMotivoAjuste(tipo: string | null | undefined): tipo is TipoAjusteConMotivo {
  return tipo === 'positivo' || tipo === 'negativo';
}

const SIGNOS_AMBOS: MotivoAjusteSignoFromApi[] = ['positivo', 'negativo'];

export function normalizeMotivoAjuste(raw: Record<string, unknown>): MotivoAjusteInventario | null {
  const codigo = String(raw.codigo ?? raw.Codigo ?? '').trim();
  if (!codigo) return null;
  const label = String(raw.label ?? raw.Label ?? raw.descripcion ?? raw.Descripcion ?? codigo).trim();
  const signosRaw = raw.signos ?? raw.Signos ?? raw.tipos ?? raw.Tipos ?? [];
  const signosParsed: MotivoAjusteSignoFromApi[] = Array.isArray(signosRaw)
    ? signosRaw
        .map((s) => String(s).trim().toLowerCase())
        .filter((s): s is MotivoAjusteSignoFromApi => s === 'positivo' || s === 'negativo')
    : [];
  // Catálogo Motivo legacy no trae signos: aplica a +/-.
  const signos = signosParsed.length > 0 ? signosParsed : SIGNOS_AMBOS;
  const activoRaw = raw.activo ?? raw.Activo;
  const activo = activoRaw === undefined || activoRaw === null ? true : Boolean(activoRaw);
  return { codigo, label, signos, activo };
}

type MotivoAjusteSignoFromApi = 'positivo' | 'negativo';

export function getMotivosPorTipo(
  motivos: MotivoAjusteInventario[],
  tipo: string | null | undefined,
): MotivoAjusteInventario[] {
  if (!requiereMotivoAjuste(tipo)) return [];
  return motivos.filter(
    (m) =>
      m.activo !== false &&
      (m.signos.length === 0 || m.signos.includes(tipo)),
  );
}

export function labelMotivo(
  codigo: string | null | undefined,
  motivos: MotivoAjusteInventario[] = [],
  fallbackLabel?: string | null,
): string {
  if (!codigo) return '';
  if (fallbackLabel && String(fallbackLabel).trim() !== '') return String(fallbackLabel).trim();
  const entry = motivos.find((m) => m.codigo === codigo);
  return entry?.label ?? codigo;
}

export function esMotivoValidoParaTipo(
  codigo: string | null | undefined,
  tipo: string | null | undefined,
  motivos: MotivoAjusteInventario[] = [],
): boolean {
  if (!requiereMotivoAjuste(tipo) || !codigo) return false;
  return getMotivosPorTipo(motivos, tipo).some((m) => m.codigo === codigo);
}

/** Extrae motivoCodigo de respuestas API (camel/Pascal) o null. */
export function mapMotivoCodigoFromApi(raw: Record<string, unknown> | null | undefined): string | null {
  if (!raw) return null;
  const v = raw.motivoCodigo ?? raw.MotivoCodigo ?? raw.motivo ?? raw.Motivo;
  if (v == null) return null;
  const s = String(v).trim();
  return s !== '' ? s : null;
}

export function mapMotivoLabelFromApi(raw: Record<string, unknown> | null | undefined): string | null {
  if (!raw) return null;
  const v = raw.motivoLabel ?? raw.MotivoLabel ?? raw.descripcionMotivo ?? raw.DescripcionMotivo;
  if (v == null) return null;
  const s = String(v).trim();
  return s !== '' ? s : null;
}

/** Payload create: incluye motivoCodigo solo en +/-. */
export function withMotivoEnPayloadCreate<T extends Record<string, unknown>>(
  payload: T,
  motivoCodigo: string | null | undefined,
  tipo: string,
): T & { motivoCodigo?: string } {
  if (!requiereMotivoAjuste(tipo)) {
    const { motivoCodigo: _drop, ...rest } = payload as T & { motivoCodigo?: string };
    return rest as T;
  }
  const codigo = motivoCodigo && String(motivoCodigo).trim() !== '' ? String(motivoCodigo).trim() : undefined;
  return codigo ? { ...payload, motivoCodigo: codigo } : { ...payload };
}
